'use client';

import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/lib/i18n';

/** Scope values mirror the BE enum: Company | Workspace | Department.
 *  User-facing labels reworded to "Org / Department / Team" per ticket
 *  #235. Internal enum values stay so the BE contract is unchanged. */
export type LeaderboardScope = 'Workspace' | 'Company' | 'Department';

const SCOPES: Array<{ value: LeaderboardScope; labelKey: string }> = [
  { value: 'Company', labelKey: 'portal.leaderboardScope.org' },
  { value: 'Department', labelKey: 'portal.leaderboardScope.department' },
  { value: 'Workspace', labelKey: 'portal.leaderboardScope.team' },
];

export function LeaderboardScopeToggle({
  value,
  onValueChange,
  className,
}: {
  value: LeaderboardScope;
  onValueChange: (v: LeaderboardScope) => void;
  className?: string;
}) {
  const { t } = useTranslation();
  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={(next) => {
        // Radix sends "" when the user clicks the active item — ignore so the
        // toggle remains a strict 3-way choice (no "all off" state).
        if (!next) return;
        onValueChange(next as LeaderboardScope);
      }}
      className={cn('inline-flex rounded-full bg-muted p-1', className)}
    >
      {SCOPES.map((s) => (
        <ToggleGroupItem
          key={s.value}
          value={s.value}
          aria-label={t(s.labelKey)}
          className={cn(
            'rounded-full px-4 py-1.5 text-sm text-muted-foreground transition-colors',
            'hover:bg-transparent hover:text-foreground',
            'data-[state=on]:bg-foreground data-[state=on]:text-background data-[state=on]:shadow-sm',
            'data-[state=on]:hover:bg-foreground data-[state=on]:hover:text-background',
          )}
        >
          {t(s.labelKey)}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
